import * as ex from 'excalibur';
import { EntityType, AnimalType, BehaviorType } from '../types/enums';
import { AnimalConfig } from '../types/interfaces';
import { MOVEMENT_SPEEDS, TILE_SIZE } from '../config/constants';
import { Animal } from '../core/animal';
import { Grid } from '../core/grid';
import { SpriteManager } from '../managers/spriteManager';

export class AnimalFactory {
  
  // Create an animal instance with type-specific configuration
  public static createAnimal(
    animalType: AnimalType,
    gridX: number,
    gridY: number,
    grid: Grid
  ): Animal {
    // Base configuration common to all animals
    const baseConfig: Partial<AnimalConfig> = {
      type: EntityType.Animal,
      gridX,
      gridY,
      width: TILE_SIZE * 0.6,
      height: TILE_SIZE * 0.6,
      animalType
    };
    
    // Animal-specific configurations
    let config: AnimalConfig;
    
    switch (animalType) {
      case AnimalType.Wolf:
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#777777'), // Gray
          behaviors: [BehaviorType.Predator, BehaviorType.Wander],
          moveSpeed: MOVEMENT_SPEEDS.FAST,
          territory: 10,
          prey: [AnimalType.Rabbit, AnimalType.Deer]
        } as AnimalConfig;
        break;
        
      case AnimalType.Deer:
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#A86342'), // Brown
          behaviors: [BehaviorType.Prey, BehaviorType.Flock, BehaviorType.Wander],
          moveSpeed: MOVEMENT_SPEEDS.NORMAL,
          territory: 8,
          flockDistance: 3,
          predators: [AnimalType.Wolf, AnimalType.Bear]
        } as AnimalConfig;
        break;
        
      case AnimalType.Rabbit:
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#E5E5E5'), // Light gray
          behaviors: [BehaviorType.Prey, BehaviorType.Random],
          moveSpeed: MOVEMENT_SPEEDS.VERY_FAST,
          predators: [AnimalType.Wolf, AnimalType.Bear]
        } as AnimalConfig;
        break;
        
      case AnimalType.Bear:
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#663300'), // Dark brown
          behaviors: [BehaviorType.Predator, BehaviorType.Wander],
          moveSpeed: MOVEMENT_SPEEDS.SLOW,
          territory: 12,
          prey: [AnimalType.Rabbit, AnimalType.Deer, AnimalType.Fish]
        } as AnimalConfig;
        break;
        
      case AnimalType.Bird:
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#FFA500'), // Orange
          behaviors: [BehaviorType.Flock, BehaviorType.Random],
          moveSpeed: MOVEMENT_SPEEDS.FAST,
          flockDistance: 2
        } as AnimalConfig;
        break;
        
      case AnimalType.Fish:
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#40E0D0'), // Turquoise
          behaviors: [BehaviorType.Flock, BehaviorType.Prey, BehaviorType.Wander],
          moveSpeed: MOVEMENT_SPEEDS.NORMAL,
          territory: 5,
          flockDistance: 2,
          predators: [AnimalType.Bear]
        } as AnimalConfig;
        break;
        
      default:
        // Default configuration for unknown animal types
        config = {
          ...baseConfig,
          color: ex.Color.fromHex('#AAAAAA'),
          behaviors: [BehaviorType.Random],
          moveSpeed: MOVEMENT_SPEEDS.NORMAL
        } as AnimalConfig;
    }
    
    // Create the animal instance
    const animal = new Animal({
      ...config,
      grid
    });
    
    // Apply sprite or animation if available
    SpriteManager.applySprite(animal);
    
    return animal;
  }
  
  // Create a random mix of animals within a region
  public static populateArea(
    grid: Grid,
    startX: number,
    startY: number,
    width: number,
    height: number,
    density: number = 0.1
  ): Animal[] {
    const animals: Animal[] = [];
    
    // Get the valid animal types to distribute
    const animalTypes = Object.values(AnimalType);
    
    // Calculate number of animals to place based on area and density
    const area = width * height;
    const numAnimals = Math.floor(area * density);
    
    // Place animals randomly
    for (let i = 0; i < numAnimals; i++) {
      // Pick random position within area
      const gridX = startX + Math.floor(Math.random() * width);
      const gridY = startY + Math.floor(Math.random() * height);
      
      // Pick random animal type
      const animalType = animalTypes[Math.floor(Math.random() * animalTypes.length)];
      
      // Check if this position is valid for this type of animal
      if (!grid.isValidMove(gridX, gridY, animalType)) {
        continue;
      }
      
      // Create animal
      const animal = this.createAnimal(animalType, gridX, gridY, grid);
      
      // Add to grid and result list
      grid.addEntity(animal, gridX, gridY);
      animals.push(animal);
    }
    
    return animals;
  }
  
  // Create fish specifically in water tiles
  public static populateWaterWithFish(grid: Grid, density: number = 0.2): Animal[] {
    const fish: Animal[] = [];
    
    // Find all water tiles in the grid
    for (let y = 0; y < grid.getHeight(); y++) {
      for (let x = 0; x < grid.getWidth(); x++) {
        // Check if this is a water tile
        if (grid.getTileAt(x, y) === 1) { // 1 is TileType.Water
          // Skip if already occupied
          if (grid.getEntityAt(x, y)) {
            continue;
          }
          
          // Only add fish based on density
          if (Math.random() < density) {
            // Create a fish
            const fishAnimal = this.createAnimal(AnimalType.Fish, x, y, grid);
            
            // Add to grid and result list
            grid.addEntity(fishAnimal, x, y);
            fish.push(fishAnimal);
          }
        }
      }
    }
    
    return fish;
  }
}